import HappyDocMascot from "@/assets/hd-icons/happydoc-mascot-sticker.svg?react";
import { PIMSDisplay, PIMSType } from "@/common/utils/types";
import {
    PROGRESS_DOT_COUNT,
    ROLE_OPTIONS,
    SCOUT_SPEECH,
    STEP_PROGRESS,
    type SetupContext,
    type SetupStepId,
} from "./setup-flow-config";

interface Props {
    step: SetupStepId;
    context: SetupContext;
}

export default function SetupLeftPanel({ step, context }: Props) {
    const speech = SCOUT_SPEECH[step];
    const progress = STEP_PROGRESS[step];

    // Interpolate {name} in speech — skip common prefixes like "Dr."
    const nameParts = (context.name || "").split(" ").filter(Boolean);
    const firstName = nameParts.length > 1 && /^dr\.?$/i.test(nameParts[0]) ? nameParts[1] : nameParts[0] || "";
    const boldText = speech.bold.replace("{name}", firstName);

    return (
        <div
            className="relative hidden w-[38%] shrink-0 flex-col items-start justify-start overflow-hidden px-32 pt-32 pb-10 md:flex"
            style={{
                backgroundImage: "url(/setup-bg-full.png)",
                backgroundSize: "auto 100%",
                backgroundPosition: "0% 50%",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#183B56",
            }}
        >
            {/* Scout icon + speech */}
            <div className="flex flex-col items-start">
                <HappyDocMascot className="h-[12rem] w-[12rem] drop-shadow-lg -m-8" />

                <div className="mt-4 max-w-[30rem] text-left whitespace-pre-line" aria-live="polite">
                    <p className="fraunces-soft text-4xl font-semibold text-white">{boldText}</p>
                    <p className="mt-2 text-xl text-max-400">{speech.sub}</p>
                </div>
            </div>

            {/* Context card */}
            {context.name && (
                <div className="mt-10 w-full rounded-xl border border-white/15 bg-white/10 px-6 py-5 backdrop-blur-sm">
                    <p className="text-md font-semibold uppercase tracking-wider text-white/50">Your Profile</p>
                    <p className="mt-2 text-2xl font-medium text-white">{context.name}</p>
                    {context.role && (
                        <p className="mt-0.5 text-base text-white/60">
                            {ROLE_OPTIONS.find((r) => r.value === context.role)?.label ?? context.role}
                        </p>
                    )}
                    {context.clinicResult && (
                        <>
                            <p className="mt-0.5 text-base text-white/60">{context.clinicResult.name}</p>
                            {context.clinicResult.city && context.clinicResult.state && (
                                <p className="text-base text-white/60">
                                    {context.clinicResult.city}, {context.clinicResult.state} {context.clinicResult.zip}
                                </p>
                            )}
                        </>
                    )}
                    {context.clinicName && !context.clinicResult && (
                        <p className="mt-0.5 text-base text-white/60">{context.clinicName}</p>
                    )}
                    {context.selectedPims && context.selectedPims !== "none" && (
                        <p className="mt-0.5 text-base text-white/60">
                            {PIMSDisplay[context.selectedPims as PIMSType] || context.selectedPims}
                        </p>
                    )}
                </div>
            )}

            {/* Spacer to push card + dots to bottom */}
            <div className="flex-[3]" />

            {/* Progress dots */}
            <div className="absolute inset-x-10 bottom-10 flex justify-end gap-2.5 opacity-50">
                {Array.from({ length: PROGRESS_DOT_COUNT }, (_, i) => (
                    <div
                        key={i}
                        className={`h-2.5 w-2.5 rounded-full transition-colors duration-500 ease-out motion-reduce:transition-none ${
                            i <= progress ? "bg-white" : "bg-white/30"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}
